import {Avatar, Card, Flag, IconButton} from 'platform/components';
import {Box, HStack, Link, Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TelephonyCallDetailRecord} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {getCallLinkedRecordContent} from '../../utils/getCallLinkedRecordContent';
import {getCallRecordDetailPath} from '../../utils/getCallRecordDetailPath';

interface CallLinkedRecordTileProps extends TestIdProps {
  record: TelephonyCallDetailRecord;
  /** Unlinks the record from the call; absent when the viewer may not. */
  onUnlink?: (record: TelephonyCallDetailRecord) => void;
  isUnlinking?: boolean;
}

/**
 * One record a call is linked to, drawn like a record-audit team member: the kind's icon as the
 * avatar, type and state flags, the number linking to the record, the vehicle under it and the
 * unlink button in the top-right corner. A record the viewer may not read shows the kind only.
 */
export function CallLinkedRecordTile(props: CallLinkedRecordTileProps) {
  const navigate = useNavigate();
  const {kindLabel, icon, code, flags, parameters} = getCallLinkedRecordContent(props.record);
  const isReadable = isNotNil(props.record.detail);
  const onUnlink = props.onUnlink;

  return (
    <Card variant="inlineWhite" data-testid={props['data-testid']}>
      <HStack spacing={4} align="flex-start" justify="space-between">
        <Box flex={1} minWidth={0}>
          <HStack spacing={4} align="center">
            <Box flexShrink={0}>
              <Avatar icon={icon} size="default" />
            </Box>
            <VStack spacing={1} minWidth={0}>
              <Show when={isNotNilOrEmpty(flags)}>
                <HStack spacing={1} wrap>
                  {flags.map((flag) => (
                    <Flag
                      key={flag.label}
                      label={flag.label}
                      colorScheme={flag.colorScheme}
                      isSubtle
                      size="small"
                    />
                  ))}
                </HStack>
              </Show>
              <Text size="xSmall" color="secondary" data-testid={suffixTestId('kind', props)}>
                {kindLabel}
              </Text>
              <Show when={isReadable && isNotNil(code) ? code : null}>
                {(recordCode) => (
                  <Link
                    size="small"
                    title={recordCode}
                    onClick={() => navigate(getCallRecordDetailPath(props.record))}
                    data-testid={suffixTestId('link', props)}
                  />
                )}
              </Show>
              <Show when={!isReadable}>
                <Text size="small" color="tertiary" data-testid={suffixTestId('unreadable', props)}>
                  {i18n.t('page.telephony.callDetail.recordNotReadable')}
                </Text>
              </Show>
              {parameters.map((parameter) => (
                <Text key={parameter} size="xSmall" color="secondary" overflowWrap="anywhere">
                  {parameter}
                </Text>
              ))}
            </VStack>
          </HStack>
        </Box>
        <Show when={onUnlink}>
          {(unlink) => (
            <Box flexShrink={0}>
              <IconButton
                icon="navigation/close"
                size="small"
                isDisabled={props.isUnlinking}
                aria-label={i18n.t('page.telephony.callDetail.unlinkRecord')}
                onClick={() => unlink(props.record)}
                data-testid={suffixTestId('unlink', props)}
              />
            </Box>
          )}
        </Show>
      </HStack>
    </Card>
  );
}
